import React from 'react';
import { PendingItem, relativeTime } from '../hooks/usePendingItems';

/** Au-delà, le menu devient une liste à faire défiler : c'est le rôle de la page. */
const PREVIEW_LIMIT = 3;

const TicketIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 14h5m7-2a8 8 0 01-8 8H7l-4 3V12a8 8 0 018-8h1a8 8 0 018 8z" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

/**
 * Aperçu de ce qui attend une action, sous la cloche.
 *
 * Volontairement court : trois lignes, les plus anciennes — celles qui
 * traînent. Un menu déroulant sert à décider s'il y a urgence, pas à traiter
 * une file ; au-delà, « Voir tout » ouvre la page qui, elle, est faite pour ça.
 */
const NotificationsPanel: React.FC<{
  items: PendingItem[];
  loading: boolean;
  onSelect: (item: PendingItem) => void;
  onSeeAll: () => void;
}> = ({ items, loading, onSelect, onSeeAll }) => {
  const preview = items.slice(0, PREVIEW_LIMIT);
  const remaining = items.length - preview.length;

  return (
    <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
        <span className="text-sm font-semibold text-gray-900">À traiter</span>
        {items.length > 0 && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            {items.length}
          </span>
        )}
      </div>

      {loading ? (
        <p className="px-4 py-8 text-center text-sm text-gray-400">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">Rien n'attend d'être traité.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {preview.map((item) => (
            <li key={`${item.kind}:${item.id}`}>
              <button
                type="button"
                onClick={() => onSelect(item)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
              >
                <span
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                    item.kind === 'ticket' ? 'bg-orange-50 text-orange-600' : 'bg-indigo-50 text-indigo-600'
                  }`}
                >
                  {item.kind === 'ticket' ? <TicketIcon /> : <DocumentIcon />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{item.title}</span>
                  <span className="block truncate text-xs text-gray-500">{item.subtitle}</span>
                </span>
                {item.date && (
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400">
                    {relativeTime(item.date)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!loading && (
        <button
          type="button"
          onClick={onSeeAll}
          className="w-full border-t border-gray-100 px-4 py-2.5 text-center text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-50"
        >
          {remaining > 0 ? `Voir tout (${remaining} de plus)` : 'Voir tout'}
        </button>
      )}
    </div>
  );
};

export default NotificationsPanel;
